import React, { useState, useEffect } from 'react';
import { teachersAPI, Teacher } from '../../api/api';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import './AdminPanel.css';

export const TeachersAdmin: React.FC = () => {
    const [teachers, setTeachers] = useState<Teacher[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [showConfirm, setShowConfirm] = useState(false);
    const [itemToDelete, setItemToDelete] = useState<number | null>(null);

    const handleDeleteClick = (id: number) => {
        setItemToDelete(id);
        setShowConfirm(true);
    };

    const handleConfirmDelete = async () => {
        if (itemToDelete) {
            try {
                await teachersAPI.delete(itemToDelete);
                loadTeachers();
            } catch (err) {
                alert('Ошибка при удалении');
                console.error(err);
            } finally {
                setShowConfirm(false);
                setItemToDelete(null);
            }
        }
    };

    const handleCancelDelete = () => {
        setShowConfirm(false);
        setItemToDelete(null);
    };

    // Чистые имена, как в твоём интерфейсе!
    const [formData, setFormData] = useState({
        name: '',
        position: '',
        grade: '',
        positionFull: '',
        description: '',
        image: null as File | null,
    });

    useEffect(() => {
        loadTeachers();
    }, []);

    const loadTeachers = async () => {
        try {
            setLoading(true);
            const data = await teachersAPI.getAll();
            setTeachers(data);
            setError(null);
        } catch (err) {
            setError('Не удалось загрузить преподавателей');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            setFormData(prev => ({ ...prev, image: e.target.files![0] }));
        }
    };

    const resetForm = () => {
        setFormData({ name: '', position: '', grade: '', positionFull: '', description: '', image: null });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!formData.name || !formData.position || !formData.description) {
            alert('Заполните обязательные поля!');
            return;
        }

        const submitData = new FormData();
        submitData.append('name', formData.name);
        submitData.append('position', formData.position);
        submitData.append('grade', formData.grade);
        submitData.append('positionFull', formData.positionFull);
        submitData.append('description', formData.description);
        if (formData.image) {
            submitData.append('image', formData.image);
        }

        try {
            if (editingId) {
                submitData.append('id', editingId.toString());
                await teachersAPI.update(editingId, submitData);
            } else {
                await teachersAPI.add(submitData);
            }

            resetForm();
            setShowForm(false);
            setEditingId(null);
            loadTeachers();
        } catch (err) {
            alert('Ошибка при сохранении');
            console.error(err);
        }
    };

    const handleEdit = (teacher: Teacher) => {
        setFormData({
            name: teacher.name,
            position: teacher.position || '',
            grade: teacher.grade || '',
            positionFull: teacher.positionFull || '',
            description: teacher.description,
            image: null,
        });
        setEditingId(teacher.id);
        setShowForm(true);
    };

    const cancelEdit = () => {
        setShowForm(false);
        setEditingId(null);
        resetForm();
    };

    if (loading) return <div>Загрузка...</div>;
    if (error) return <div className="error">{error}</div>;

    return (
        <div className="admin-panel">
            <div className="admin-panel-header">
                <h2>Управление преподавателями</h2>
                <button className="btn-add" onClick={() => { setShowForm(!showForm); setEditingId(null); resetForm(); }}>
                    {showForm ? 'Отмена' : '+ Добавить преподавателя'}
                </button>
            </div>

            {showForm && (
                <form className="review-form" onSubmit={handleSubmit}>
                    <h3>{editingId ? 'Редактировать преподавателя' : 'Новый преподаватель'}</h3>

                    <div className="form-group">
                        <label>ФИО: *</label>
                        <input type="text" name="name" value={formData.name} onChange={handleInputChange} required />
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label>Должность (кратко): *</label>
                            <input type="text" name="position" value={formData.position} onChange={handleInputChange} required />
                        </div>
                        <div className="form-group">
                            <label>Квалификация:</label>
                            <input type="text" name="grade" value={formData.grade} onChange={handleInputChange} />
                        </div>
                    </div>

                    <div className="form-group">
                        <label>Должность (полностью):</label>
                        <input type="text" name="positionFull" value={formData.positionFull} onChange={handleInputChange} />
                    </div>

                    <div className="form-group">
                        <label>Описание:</label>
                        <textarea name="description" value={formData.description} onChange={handleInputChange} rows={4} required />
                    </div>

                    <div className="form-group">
                        <label>Фотография:</label>
                        <input type="file" accept="image/*" onChange={handleFileChange} />
                        {formData.image && <span className="file-info">Выбран файл: {formData.image.name}</span>}
                    </div>

                    <div className="form-actions">
                        <button type="submit" className="btn-save">{editingId ? 'Сохранить' : 'Добавить'}</button>
                        <button type="button" className="btn-cancel" onClick={cancelEdit}>Отмена</button>
                    </div>
                </form>
            )}

            <div className="teachers-list">
                {teachers.length === 0 ? (
                    <p>Нет преподавателей</p>
                ) : (
                    teachers.map(teacher => (
                        <div key={teacher.id} className="teacher-item-admin">
                            {teacher.image && (
                                <img src={teacher.image.startsWith('data:') ? teacher.image : `data:image/jpeg;base64,${teacher.image}`} alt={teacher.name} className="teacher-image" />
                            )}
                            <div className="teacher-content">
                                <h4>{teacher.name}</h4>
                                {teacher.position && <div className="teacher-position">{teacher.position}</div>}
                                {teacher.grade && <div className="teacher-grade">{teacher.grade}</div>}
                                {teacher.positionFull && <div className="teacher-position-full">{teacher.positionFull}</div>}
                                <p>{teacher.description}</p>
                            </div>
                            <div className="teacher-actions">
                                <button className="btn-edit" onClick={() => handleEdit(teacher)}>✏️</button>
                                <button className="btn-delete" onClick={() => handleDeleteClick(teacher.id)}>🗑️</button>
                            </div>
                        </div>
                    ))
                )}
            </div>

            {showConfirm && itemToDelete && (
                <ConfirmDialog message="Удалить этого преподавателя?" onConfirm={handleConfirmDelete} onCancel={handleCancelDelete} />
            )}
        </div>
    );
};